import React from 'react';
import { Box, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { useTranslation } from 'services/localization';
import { useSelector } from 'react-redux';

import * as ShopSelectors from 'selectors/ShopSelectors';
import * as UserSelectors from 'selectors/UserSelectors';
import * as ActiveProductSelectors from 'selectors/ActiveProductSelectors';
import { getLocationUrlById } from 'common/utils/shopUtils';
import ExternalLink from 'components/ExternalLink';
import { isLiftTicketPackageProduct, isLiftTicketProduct } from 'common/modules/skidata';

export interface PublicSelectProps {
	value: boolean;
	onChange: (value: boolean) => void;
	disabled?: boolean;
}
const PublicSelect = (props: PublicSelectProps) => {
	const { t } = useTranslation();
	const { value, onChange, disabled } = props;
	const activeShopStoreUrl = useSelector(ShopSelectors.activeShopOnlineURL);
	const shopLocationsCount = useSelector(ShopSelectors.allShopLocationsCount);
	const shopLocationUrls = useSelector(ShopSelectors.activeShopLocationUrls);
	const activeLocationId = useSelector(UserSelectors.userActiveLocationId)!;
	const activeProduct = useSelector(ActiveProductSelectors.productData)!;

	const isLiftTicket = isLiftTicketProduct(activeProduct);

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		onChange(e.target.value === 'public');
	};

	const getEmbedLocationUrl = () => {
		if (shopLocationsCount <= 1) return '';
		const locationUrl = getLocationUrlById(activeLocationId, shopLocationUrls);
		return locationUrl ? `/l/${locationUrl}` : '';
	};

	const getProductUrl = () => {
		let productPath = '';
		if (isLiftTicketPackageProduct(activeProduct)) {
			productPath = `/packageTicket/${activeProduct.id}`;
		} else if (isLiftTicket) {
			productPath = '/tickets';
		} else {
			productPath = `/product/${activeProduct.id}`;
		}
		return `${activeShopStoreUrl}${getEmbedLocationUrl()}${productPath}`;
	};
	return (
		<Box>
			<Stack direction="row" justifyContent="space-between" alignItems="center">
				<Typography variant="h6">{t('product.status', 'Status')}</Typography>
				<ExternalLink href={getProductUrl()}>
					{value
						? t('common:actions.viewInStore', 'View in store')
						: t('common:actions.preview', 'Preview')}
				</ExternalLink>
			</Stack>
			<TextField
				sx={{
					mt: 3,
					mb: 1,
					background: value
						? (theme) => theme.palette.success.lightest
						: (theme) => theme.palette.background.secondary,
					borderColor: value ? (theme) => theme.palette.success.dark : 'initial',
				}}
				select
				variant="outlined"
				fullWidth
				value={value ? 'public' : 'hidden'}
				onChange={handleChange}
				disabled={disabled}
			>
				<MenuItem value="public">{t('common:states.public', 'Public')}</MenuItem>
				<MenuItem value="hidden" disabled={isLiftTicket}>
					{t('common:states.hidden', 'Hidden')}
				</MenuItem>
			</TextField>
			{value ? (
				<Box>
					<Typography variant="body2" color="secondary" fontSize="1.2rem">
						{t('product.publicDescription', 'This product is visible to your customers')}
					</Typography>
				</Box>
			) : (
				<Box>
					<Typography variant="body2" color="secondary" fontSize="1.2rem">
						{t(
							'product.hiddenDescriptionLong',
							'This product is not visible to your customers. It can still be booked as an additional product, as part of a product package, or by visiting the product page via a direct link.',
						)}
					</Typography>
				</Box>
			)}
		</Box>
	);
};

export default PublicSelect;
